import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Info } from "lucide-react";

import type { ContextUsedPanelProps } from "../types/chat.types";

const ContextUsedPanel: React.FC<ContextUsedPanelProps> = ({
  metadata,
  defaultExpanded = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!metadata) return null;

  const hasContent =
    metadata.contextSummary ||
    metadata.modelId ||
    metadata.tokenUsage ||
    metadata.researchSources?.length;
  if (!hasContent) return null;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700"
        aria-expanded={expanded}
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <Info className="h-3 w-3" />
        {t("Context used")}
        {metadata.cached ? (
          <span className="ml-1 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
            {t("cached")}
          </span>
        ) : null}
      </button>

      {expanded ? (
        <dl className="mt-1.5 rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
          {metadata.contextSummary?.recordType ? (
            <Row
              label={t("Record")}
              value={`${metadata.contextSummary.recordType} ${metadata.contextSummary.recordId ?? ""}`.trim()}
            />
          ) : null}
          {metadata.modelId ? <Row label={t("Model")} value={metadata.modelId} /> : null}
          {metadata.tokenUsage ? (
            <Row
              label={t("Tokens")}
              value={`${metadata.tokenUsage.total} (${metadata.tokenUsage.prompt} + ${metadata.tokenUsage.completion})`}
            />
          ) : null}
          {typeof metadata.responseTimeMs === "number" ? (
            <Row
              label={t("Response time")}
              value={`${Math.round(metadata.responseTimeMs)} ms`}
            />
          ) : null}
          {metadata.researchSources?.length ? (
            <div className="mt-1">
              <div className="font-medium text-gray-700">{t("Sources")}</div>
              <ul className="ml-3 list-disc">
                {metadata.researchSources.map((src, idx) => (
                  <li key={`${src.url}-${idx}`} className="truncate">
                    <a
                      href={src.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-blue-600 hover:underline"
                    >
                      {src.title || src.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </dl>
      ) : null}
    </div>
  );
};

const Row: React.FC<{ label: React.ReactNode; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-0.5">
    <dt className="text-gray-500">{label}</dt>
    <dd className="text-right text-gray-700">{value}</dd>
  </div>
);

export default ContextUsedPanel;
